'use client'

import { featureFlagKeys } from 'feature-flags/constants/featureFlags'
import { useFeatureFlag } from 'feature-flags/hooks/useFeatureFlag/useFeatureFlag'
import { FloatingButton } from 'fukku/Button/Floating'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver/useIntersectionObserver'
import { useReducedMotion } from 'hooks/useReducedMotion/useReducedMotion'
import { IconPauseL } from 'icons/components/IconPauseL'
import { IconPauseS } from 'icons/components/IconPauseS'
import { IconPlayL } from 'icons/components/IconPlayL'
import { IconPlayS } from 'icons/components/IconPlayS'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { dataTestIds } from 'pdp/src/constants/dataTestIds/dataTestIds'
import { useAnalytics } from 'pdp/src/contexts/AnalyticsProvider/AnalyticsProvider'
import { type AnimationEvent, useEffect, useRef, useState } from 'react'
import { isBot } from 'utils/isBot/IsBot'

import styles from './Video.module.scss'

interface VideoProps {
	url: string
	className?: string
}

export function Video({ url, className }: VideoProps): React.ReactNode {
	const { isCicd } = useMasterData()
	const { t } = useLabels()
	const reducedMotion = useReducedMotion()
	const {
		sendVideoAutoPauseEvent,
		sendVideoAutoPlayEvent,
		sendVideoEndedEvent,
		sendVideoErrorEvent,
		sendVideoPauseClickEvent,
		sendVideoPlayClickEvent,
		sendVideoViewEvent,
	} = useAnalytics()
	const [isPausedByUser, setIsPausedByUser] = useState(isCicd || reducedMotion)
	const { enabled: isNewVision } = useFeatureFlag(
		featureFlagKeys.PdpNewVisionMvp
	)

	const videoRef = useRef<HTMLVideoElement | null>(null)
	const iconRef = useRef<HTMLDivElement | null>(null)
	const startedTimeRef = useRef(new Date().getTime())
	const videoEndedTimesRef = useRef(0)

	const entry = useIntersectionObserver(videoRef, {
		threshold: 0.2,
	})

	useEffect(() => {
		if (!isCicd) {
			setIsPausedByUser(reducedMotion)
		}
	}, [reducedMotion])

	const toggle = (): void => {
		if (videoRef.current?.paused) {
			videoRef.current.play()
			sendVideoPlayClickEvent()
		} else {
			videoRef.current?.pause()
			sendVideoPauseClickEvent()
		}
		setIsPausedByUser((prev) => !prev)
	}

	const onEnded = (): void => {
		videoRef.current?.play()
		videoEndedTimesRef.current += 1

		if (videoEndedTimesRef.current === 1) {
			sendVideoEndedEvent()
		}
	}

	const onPlaying = () => {
		if (videoEndedTimesRef.current === 0) {
			sendVideoViewEvent()
		}
	}

	const onError = () => {
		const time = new Date().getTime() - startedTimeRef.current
		sendVideoErrorEvent({ time })
	}

	const onButtonAnimationEnd = (e: AnimationEvent<HTMLDivElement>): void => {
		iconRef.current?.classList.remove(styles.fadeout)

		if (e.animationName === styles['fade-out']) {
			toggle()
			iconRef.current?.classList.add(styles.fadein)
		}
	}

	const addFadeOut = (): void => {
		iconRef.current?.classList.remove(styles.fadein)
		iconRef.current?.classList.add(styles.fadeout)
	}

	const buttonWrapperClassName = isNewVision
		? styles.buttonWrapper
		: styles.buttonWrapperLegacy

	useEffect(() => {
		if (videoRef.current) {
			if (entry?.isIntersecting && !isPausedByUser && !isBot()) {
				videoRef.current?.play()
				sendVideoAutoPlayEvent()
			} else if (!videoRef.current?.paused) {
				videoRef.current?.pause()
				sendVideoAutoPauseEvent()
			} else {
				// Do nothing
			}
		}
	}, [entry?.isIntersecting])

	return (
		<div className={styles.video}>
			<video
				src={url}
				onEnded={onEnded}
				onError={onError}
				onPlaying={onPlaying}
				className={className}
				ref={videoRef}
				data-testid={dataTestIds['pdp.gallery.video']}
				muted
				playsInline
			>
				<source src={url} type='video/mp4' />
				<track kind='captions' />
			</video>
			<div className={buttonWrapperClassName}>
				<FloatingButton
					onClick={addFadeOut}
					icon={
						<div ref={iconRef} onAnimationEnd={onButtonAnimationEnd}>
							{isNewVision ? (
								<>
									<IconPlayL
										height={20}
										width={20}
										className={isPausedByUser ? styles.show : styles.hide}
										alt={t('accessibility.pdp.play.button')}
									/>
									<IconPauseL
										height={20}
										width={20}
										className={isPausedByUser ? styles.hide : styles.show}
										alt={t('accessibility.pdp.pause.button')}
									/>
								</>
							) : (
								<>
									<IconPlayS
										height={16}
										width={16}
										className={isPausedByUser ? styles.show : styles.hide}
										alt={t('accessibility.pdp.play.button')}
									/>
									<IconPauseS
										height={16}
										width={16}
										className={isPausedByUser ? styles.hide : styles.show}
										alt={t('accessibility.pdp.pause.button')}
									/>
								</>
							)}
						</div>
					}
				/>
			</div>
		</div>
	)
}
